import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { OreZoneMarker } from '@/versions/v1/schema/ore-zone';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { ORE_ZONES } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import {
  formatDeltaGrade,
  metOperatingCurves,
  type OperatingCurveProjection,
} from '@/versions/v1/shared/operating-curve.derive';
import { formatValue } from '@/versions/v1/shared/reading.derive';
import { recoveryUncertainty, periodWindows } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';

export type Metal = 'Zn' | 'Pb' | 'Ag';

const METAL_READINGS: Record<Metal, { x: string; y: string }> = {
  Zn: { x: 'rd-kpi-zn-grade', y: 'rd-kpi-zn-rec' },
  Pb: { x: 'rd-kpi-pb-grade', y: 'rd-kpi-pb-rec' },
  Ag: { x: 'rd-kpi-ag-grade', y: 'rd-kpi-ag-rec' },
};

export type LedgerRow = {
  label: string;
  grade: string;
  recovery: string;
  tooltip?: string;
};

/** The Met mount's `7 d · N of M on the pair` line (Q6) — a single-field
 * row, not a grade/recovery pair. Superseded by `zoneRows` when zones are
 * on the trail (curve-zone #286). */
export type SevenDayRow = {
  label: string;
  text: string;
  tooltip?: string;
};

/** Per-zone mean grade delta against the working curve — one ledger row each. */
export type ZoneLedgerRow = {
  label: string;
  text: string;
  marker: OreZoneMarker;
  tooltip?: string;
};

export type ZoneLegendEntry = {
  id: string;
  label: string;
  marker: OreZoneMarker;
  provenance: string;
};

export type TabItem = {
  id: Metal;
  label: string;
  hasFuture: boolean;
  tone: VizTone;
};

export type LedgerModel = {
  tabItems: TabItem[];
  designRow: LedgerRow;
  nowRow: LedgerRow;
  futureRow: LedgerRow | null;
  sevenDayRow: SevenDayRow | null;
  zoneRows: readonly ZoneLedgerRow[];
  zoneLegend: readonly ZoneLegendEntry[];
};

function reading(id: string) {
  const row = READINGS.find((r) => r.id === id);
  if (!row) throw new Error(`Unknown reading ${id}`);
  return row;
}

function zoneLegendForTrail(
  curves: readonly (OperatingCurveProjection & { metal: string })[],
): ZoneLegendEntry[] {
  const ids = new Set(
    curves.flatMap((curve) => curve.trail.map((pt) => pt.zone?.id).filter(Boolean)),
  );
  return ORE_ZONES.filter((zone) => ids.has(zone.id)).map((zone) => ({
    id: zone.id,
    label: zone.label,
    marker: zone.marker,
    provenance: zone.provenance,
  }));
}

export function buildLedgerModel(
  curves: readonly (OperatingCurveProjection & { metal: string })[],
  metal: Metal,
  opts: { showSevenDayRow?: boolean } = {},
): LedgerModel {
  const tabItems: TabItem[] = curves.map((row) => ({
    id: row.metal as Metal,
    label: row.metal,
    hasFuture: Boolean(row.future),
    tone: row.tone,
  }));

  const curve = curves.find((row) => row.metal === metal) ?? curves[0]!;
  const { x: xId, y: yId } = METAL_READINGS[metal];
  const xReading = reading(xId);
  const yReading = reading(yId);

  const designRow: LedgerRow = {
    label: 'Design',
    grade: formatValue(xReading, { value: curve.design.x, withUnit: true }),
    recovery: formatValue(yReading, { value: curve.design.y, withUnit: true }),
    tooltip: curve.designRowTooltip,
  };

  const recoveryUnc = recoveryUncertainty(metal, periodWindows().day);
  const nowRow: LedgerRow = {
    label: 'Now',
    grade: formatValue(xReading, { value: curve.current.x, withUnit: true }),
    recovery: formatValue(yReading, { value: curve.current.y, withUnit: true }),
    tooltip: recoveryUnc
      ? `${curve.current.label} · recovery ± ${recoveryUnc.margin.toFixed(1)} % (${recoveryUnc.tooltip})`
      : curve.current.label,
  };

  let futureRow: LedgerRow | null = null;
  if (curve.future) {
    futureRow = {
      label: curve.future.label,
      grade: formatValue(xReading, { value: curve.future.x, withUnit: true }),
      recovery: formatValue(yReading, { value: curve.future.y, withUnit: true }),
    };
  } else if (curve.futureRowPlaceholder) {
    futureRow = {
      label: curve.futureRowPlaceholder.label,
      grade: '—',
      recovery: '—',
      tooltip: curve.futureRowPlaceholder.tooltip,
    };
  }

  let sevenDayRow: SevenDayRow | null = null;
  let zoneRows: ZoneLedgerRow[] = [];
  const zoneLegend = zoneLegendForTrail(curves);
  if (curve.curveJudgement) {
    const { onPairCount, total, offDays, zoneRows: judgementZoneRows } = curve.curveJudgement;
    if (judgementZoneRows && judgementZoneRows.length > 0) {
      zoneRows = judgementZoneRows.map((row) => ({
        label: row.label,
        text: `${row.dayCount} d · grade ${formatDeltaGrade(row.meanDeltaGradePct)} vs pair`,
        marker: row.marker,
        tooltip: row.tooltip,
      }));
    } else if (opts.showSevenDayRow) {
      const tooltip =
        offDays.length > 0
          ? offDays.map((d) => `${d.label} (${formatDeltaGrade(d.deltaGradePct)})`).join(', ')
          : 'every day on the pair';
      sevenDayRow = {
        label: '7 d',
        text: `${onPairCount} of ${total} on the pair`,
        tooltip,
      };
    }
  }

  return { tabItems, designRow, nowRow, futureRow, sevenDayRow, zoneRows, zoneLegend };
}

export function projectOperatingCurvePanel(
  state: SessionState,
  opts: { omitFuture?: boolean } = {},
) {
  const curves = metOperatingCurves(state, opts);
  return { curves };
}

export {
  THROUGHPUT_FRONTIER_PROVENANCE,
  grindOperatingCurve,
  metOperatingCurves,
  operatingCurve,
  throughputFrontierYAt,
  type OperatingCurveJudgementSummary,
  type OperatingCurvePair,
  type OperatingCurvePoint,
  type OperatingCurveProjection,
  type OperatingCurveSentence,
  type OperatingCurveZoneRow,
} from '@/versions/v1/shared/operating-curve.derive';
